import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import DatePicker from "react-datepicker";
import { getOrdersReport, updateOrderPayment, updateRoomOrderPayment } from '@utilities/order';
import ReactSelect from '@components/Select';
import Switch from '@mui/material/Switch';
import Grid from '@mui/material/Grid';
import { getAnalyticsReport } from '@utilities/common';
import { ExportToCsv } from 'export-to-csv';
import AgGrid from '@components/AgGrid';
import { FilterPaymentType } from '@src/constants/common';
import moment from 'moment';
import {getUpdatedColDefs} from './colDefs';
import {
    buildRoomBillSnapshot,
    logOrderEventAsync,
} from '@utilities/orderLogging';

import "react-datepicker/dist/react-datepicker.css";

import { Button, ButtonGroup, Dialog, IconButton, Typography, TextField, FormControl, InputLabel, Select as MuiSelect, MenuItem, AppBar, Toolbar } from '@mui/material';
import Box from '@mui/material/Box';
import MuiButtonGroup from '@mui/material/ButtonGroup';
import CloseIcon from '@mui/icons-material/Close';
import ImagePreviewModal from '@components/ImagePreviewModal';
import DeleteIcon from '@mui/icons-material/RemoveCircleOutline';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DemoContainer, DemoItem } from '@mui/x-date-pickers/internals/demo';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import "./Report.scss";
import { printFoodOrderBill } from '@src/app/components/Print';
import { getRoomPdfHTMLString, getRoomPdfHTMLStrings } from '@src/app/components/Print/PrintRoomPDF';
import { printPDF } from '@src/app/components/Print/PrintUtility';
import { createRoomBills } from '@utilities/auth';
import FileUpload from '@components/FileUpload';
import { useBulkTestBilling } from './hooks/useBulkTestBilling';
import { useDownloadBills } from './hooks/useDownloadBills';
import BulkTestBillingModal from './components/BulkTestBillingModal';
import { logError } from '@src/app/actions/userActions';
import UpdateOrderDialog from '../Orders/UpdateOrderDialog';

const label = { inputProps: { 'aria-label': 'Switch demo' } };

const EditTransition = React.forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

function DateLabel({ componentName }: { componentName: string }) {
    return <span><strong>{componentName}</strong></span>;
}

const EditInitialFormState: any = {
    uoId: '',
    guestName: '',
    guestMobile: '',
    guestAddress: '',
    guestOther: '',
    guestExtraInfo: '',
    guestCompanyName: '',
    guestCompanyGST: '',
    checkInDate: new Date()?.toISOString(),
    checkOutDate: null,
    discount: 0,
    rooms: [{ roomType: '', roomCost: '', roomCustomCost: '', noOfNights: 1, roomNumber: '' }],
    identificationFiles: [],
};

const cloneOrderForEdit = (order: any) => ({
    ...order,
    rooms: (order?.rooms || []).map((room: any) => ({ ...room })),
    identificationFiles: [...(order?.identificationFiles || [])],
});

// Function to format rooms array into readable string
const formatRoomsString = (rooms: any[]): string => {
    if (!rooms || !Array.isArray(rooms) || rooms.length === 0) {
        return '';
    }
    
    return rooms.map((room, index) => {
        const roomInfo = `${room?.roomType || 'N/A'} -> ${room?.roomNumber || 'N/A'} -> ${room?.roomCustomCost || 'N/A'} -> ${room?.noOfNights || 'N/A'} nights`;
        return index === 0 ? roomInfo : `\n${roomInfo}`;
    }).join('');
};

type RoomLine = {
    roomType?: string;
    roomCustomCost?: string | number;
    noOfNights?: string | number;
};

const getRoomLines = (rooms: unknown): RoomLine[] => {
    if (!rooms || !Array.isArray(rooms)) {
        return [];
    }
    return rooms.filter((room) => room && typeof room === 'object');
};

const formatRoomType = (rooms: unknown): string => {
    const lines = getRoomLines(rooms);
    if (!lines.length) {
        return '';
    }
    const types = Array.from(new Set(lines.map((room) => room?.roomType?.trim() || 'N/A')));
    return types.length === 1 ? types[0] : types.join(', ');
};

/** Totals aligned with billing: itemsCost = Σ (roomCustomCost × noOfNights). */
const summarizeRoomBilling = (rooms: unknown) => {
    const lines = getRoomLines(rooms);
    let totalNights = 0;
    let lineCharges = 0;
    for (const room of lines) {
        const nights = Number(room?.noOfNights || 0);
        const rate = Number(room?.roomCustomCost || 0);
        if (nights > 0) {
            totalNights += nights;
            lineCharges += rate * nights;
        }
    }
    return {
        roomCount: lines.length,
        totalNights,
        lineCharges,
    };
};

const formatRoomCount = (rooms: unknown): string => {
    const { roomCount } = summarizeRoomBilling(rooms);
    return roomCount > 0 ? String(roomCount) : '';
};

/** Total room-nights (sum across all rooms), not max stay length. */
const formatNoOfNights = (rooms: unknown): string => {
    const { totalNights } = summarizeRoomBilling(rooms);
    return totalNights > 0 ? String(totalNights) : '';
};

/**
 * Weighted average tariff per room-night for GST export.
 * ratePerNight × noOfNights should match roomCharges (itemsCost).
 */
const formatRoomTariff = (rooms: unknown, itemsCost?: number): string => {
    const { totalNights, lineCharges } = summarizeRoomBilling(rooms);
    if (totalNights <= 0) {
        return '';
    }
    const gross =
        itemsCost !== undefined && itemsCost !== null && !Number.isNaN(Number(itemsCost))
            ? Number(itemsCost)
            : lineCharges;
    return (gross / totalNights).toFixed(2);
};

type MenuCatalog = Record<string, Record<string, { sacCode?: string | number; hsnCode?: string | number }>>;

type CartLine = {
    selected?: number;
    selectedVariants?: { value?: number };
    category?: string;
    name?: string;
    sacCode?: string | number;
    hsnCode?: string | number;
    restaurant?: { restaurantSAC?: string | number };
};

type ResolvedCartCodes = {
    sacCode: string;
    hsnCode: string;
};

const findMenuItemForCartLine = (
    menu: MenuCatalog | undefined,
    cartKey: string,
    item: CartLine
): { sacCode?: string | number; hsnCode?: string | number } | null => {
    if (!menu || typeof menu !== 'object') {
        return null;
    }

    const category = item?.category;
    const name = item?.name;
    if (category && name && menu[category]?.[name]) {
        return menu[category][name];
    }

    for (const cat of Object.keys(menu)) {
        const categoryItems = menu[cat];
        if (!categoryItems || typeof categoryItems !== 'object') {
            continue;
        }
        for (const itemName of Object.keys(categoryItems)) {
            const prefix = `${cat}-${itemName}-`;
            if (cartKey === `${cat}-${itemName}` || cartKey.startsWith(prefix)) {
                return categoryItems[itemName];
            }
        }
    }

    return null;
};

/** Prefer latest menu codes; fall back to values stored on the order cart line. */
const resolveCartLineCodes = (
    item: CartLine,
    cartKey: string,
    menu?: MenuCatalog
): ResolvedCartCodes => {
    const menuItem = findMenuItemForCartLine(menu, cartKey, item);
    const menuSac = normalizeCode(menuItem?.sacCode);
    const menuHsn = normalizeCode(menuItem?.hsnCode);
    const cartSac = normalizeCode(item?.sacCode);
    const cartHsn = normalizeCode(item?.hsnCode);

    return {
        sacCode: menuSac || cartSac,
        hsnCode: menuHsn || cartHsn,
    };
};

const normalizeCode = (code: unknown): string => {
    if (code === undefined || code === null) {
        return '';
    }
    return String(code).trim();
};

const getOrderRestaurantSAC = (element: any, defaultRestaurantSAC?: string): string => {
    return (
        normalizeCode(element?.restaurant?.restaurantSAC) ||
        normalizeCode(defaultRestaurantSAC)
    );
};

const getCartLineRestaurantSAC = (item: CartLine, fallbackRestaurantSAC?: string): string => {
    return (
        normalizeCode(item?.restaurant?.restaurantSAC) ||
        normalizeCode(fallbackRestaurantSAC)
    );
};

const getCartLineCost = (item: CartLine): number => {
    const qty = Number(item?.selected || 0);
    const unitPrice = Number(item?.selectedVariants?.value || 0);
    if (!qty || !unitPrice) {
        return 0;
    }
    return Number((qty * unitPrice).toFixed(2));
};

const addCodeTotal = (totals: Record<string, number>, columnKey: string, amount: number): void => {
    totals[columnKey] = Number(((totals[columnKey] || 0) + amount).toFixed(2));
};

const formatCodeTotals = (totals: Record<string, number>): Record<string, string> => {
    const result: Record<string, string> = {};
    Object.entries(totals).forEach(([key, amount]) => {
        result[key] = amount.toFixed(2);
    });
    return result;
};

/**
 * SAC columns: item sacCode, else restaurantSAC when sac/hsn missing on item.
 * Items with hsnCode are counted under HSN columns only (no double count).
 */
const getSACItemCost = (
    cart: unknown,
    restaurantSAC?: string,
    menu?: MenuCatalog
): Record<string, string> => {
    const totals: Record<string, number> = {};
    if (!cart || typeof cart !== 'object') {
        return formatCodeTotals(totals);
    }

    Object.entries(cart as Record<string, CartLine>).forEach(([cartKey, item]) => {
        if (!item?.selected) {
            return;
        }
        const cost = getCartLineCost(item);
        if (!cost) {
            return;
        }

        const { sacCode: resolvedSac, hsnCode: resolvedHsn } = resolveCartLineCodes(item, cartKey, menu);
        if (resolvedHsn) {
            return;
        }

        const sacCode = resolvedSac || getCartLineRestaurantSAC(item, restaurantSAC);
        if (!sacCode) {
            return;
        }

        addCodeTotal(totals, `SAC_${sacCode}`, cost);
    });

    return formatCodeTotals(totals);
};

/** HSN columns: items with hsnCode only. */
const getHSNItemCost = (cart: unknown, menu?: MenuCatalog): Record<string, string> => {
    const totals: Record<string, number> = {};
    if (!cart || typeof cart !== 'object') {
        return formatCodeTotals(totals);
    }

    Object.entries(cart as Record<string, CartLine>).forEach(([cartKey, item]) => {
        if (!item?.selected) {
            return;
        }
        const cost = getCartLineCost(item);
        if (!cost) {
            return;
        }

        const { hsnCode: resolvedHsn } = resolveCartLineCodes(item, cartKey, menu);
        if (!resolvedHsn) {
            return;
        }

        addCodeTotal(totals, `HSN_${resolvedHsn}`, cost);
    });

    return formatCodeTotals(totals);
};

const collectSacHsnColumnKeys = (
    reportRows: any[],
    defaultRestaurantSAC?: string,
    menu?: MenuCatalog
): { sacKeys: string[]; hsnKeys: string[] } => {
    const sacKeys = new Set<string>();
    const hsnKeys = new Set<string>();

    reportRows.forEach((element) => {
        const restaurantSAC = getOrderRestaurantSAC(element, defaultRestaurantSAC);
        Object.keys(getSACItemCost(element?.cart, restaurantSAC, menu)).forEach((key) => sacKeys.add(key));
        Object.keys(getHSNItemCost(element?.cart, menu)).forEach((key) => hsnKeys.add(key));
    });

    return {
        sacKeys: Array.from(sacKeys).sort(),
        hsnKeys: Array.from(hsnKeys).sort(),
    };
};

const buildCodeColumnValues = (
    allKeys: string[],
    codeTotals: Record<string, string>
): Record<string, string> => {
    return allKeys.reduce((acc: Record<string, string>, key) => {
        acc[key] = codeTotals[key] || '';
        return acc;
    }, {});
};

const buildReportExportRows = (
    reports: any[],
    isRoomReport: boolean,
    restaurantSAC?: string,
    menu?: MenuCatalog
): any[] => {
    const temp: any[] = [];
    const { sacKeys, hsnKeys } = !isRoomReport
        ? collectSacHsnColumnKeys(reports, restaurantSAC, menu)
        : { sacKeys: [], hsnKeys: [] };

    reports.forEach((element: any) => {
        let tApx = '';
        if (element?.cart) {
            Object.keys(element.cart).forEach((cItem: any) => {
                const cartLine = element.cart[cItem];
                if (cartLine?.selected) {
                    const { sacCode: resolvedSac, hsnCode: resolvedHsn } = resolveCartLineCodes(
                        cartLine,
                        cItem,
                        menu
                    );
                    const codeLabel =
                        resolvedSac ||
                        resolvedHsn ||
                        cartLine?.restaurant?.restaurantSAC ||
                        '';
                    tApx += `${cartLine.selected} X ${cItem} (${codeLabel}),\n`;
                }
            });
        }

        const gstTotal = Number(element?.gstBill || 0);
        const cGstAmount = Number((gstTotal / 2).toFixed(2));
        const sGstAmount = Number((gstTotal - cGstAmount).toFixed(2));
        const orderRestaurantSAC = getOrderRestaurantSAC(element, restaurantSAC);
        const sacColumns = buildCodeColumnValues(
            sacKeys,
            getSACItemCost(element?.cart, orderRestaurantSAC, menu)
        );
        const hsnColumns = buildCodeColumnValues(hsnKeys, getHSNItemCost(element?.cart, menu));

        temp.push(
            !isRoomReport
                ? {
                      orderId: element?.orderSeqNo || element?.orderId,
                      billDate: element?.createdAt,
                      itemsCost: element?.itemsCost,
                      lessDiscount: element?.discount,
                      taxableValue: element?.itemsCost - element?.discount,
                      gstCharges: gstTotal.toFixed(2),
                      cGstCharges: cGstAmount.toFixed(2),
                      sGstCharges: sGstAmount.toFixed(2),
                      invoiceTotal: element?.totalCost,
                      paymentMode: element?.selectedPayment,
                      table: element?.tableNumber,
                      customerName: element?.customerName || '',
                      customerPhone: element?.customerPhone || '',
                      customerGstNumber: element?.customerGstNumber || '',
                      customerBillingAddress: element?.customerBillingAddress || '',
                      itemsList: tApx,
                      ...sacColumns,
                      ...hsnColumns,
                  }
                : {
                      orderId: element?.orderSeqNo || element?.uoId,
                      billDate: element?.createdAt,
                      paymentMode: element?.selectedPayment,
                      rooms: formatRoomsString(element?.rooms),
                      roomType: formatRoomType(element?.rooms),
                      roomCount: formatRoomCount(element?.rooms),
                      ratePerNight: formatRoomTariff(element?.rooms, element?.itemsCost),
                      noOfNights: formatNoOfNights(element?.rooms),
                      roomCharges: element?.itemsCost,
                      lessDiscount: element?.discount,
                      taxableValue: element?.itemsCost - element?.discount,
                      gstRatePercent: element?.gstRate,
                      gstCharges: gstTotal.toFixed(2),
                      cGstCharges: cGstAmount.toFixed(2),
                      sGstCharges: sGstAmount.toFixed(2),
                      invoiceTotal: element?.itemsCost - element?.discount + gstTotal,
                      sacCode: element?.roomSAC || '996311',
                      guestName: element?.guestName || '',
                      guestAddress: element?.guestAddress || '',
                      guestOther: element?.guestOther || '',
                      guestCompanyName: element?.guestCompanyName || '',
                      guestCompanyGST: element?.guestCompanyGST || '',
                      guestExtraInfo: element?.guestExtraInfo || '',
                      checkInDate: element?.checkInDate || '',
                      checkOutDate: element?.checkOutDate || '',
                  }
        );
    });

    return temp;
};

const getReportExportFilename = (isRoomReport: boolean, startDate: Date, endDate: Date): string =>
    `[${isRoomReport ? 'Room' : 'Food'}]${moment(startDate).format('D-MMM-YYYY')} - ${moment(endDate).format('D-MMM-YYYY')}`;

const serviceTypeOptions = [
    { label: 'Food Service', value: 0 },
    { label: 'Room Service', value: 1 },
];

const Reports = ({ reports, restaurant, isSuperAdmin, user, roomList, roomGSTSlab }: any) => {

    const [paymentType, setPaymentType] = React.useState(FilterPaymentType[0].value);
    const [localReportData, setLocalReportData] = React.useState(reports);
    const options = { 
        fieldSeparator: ',',
        quoteStrings: '"',
        decimalSeparator: '.',
        showLabels: true, 
        showTitle: true,
        title: '',
        useTextFile: false,
        useBom: true,
        useKeysAsHeaders: true,
        // headers: ['Column 1', 'Column 2', etc...] <-- Won't work with useKeysAsHeaders present!
      };
    
    const [checked, setChecked]:any = useState(true);
 
    let dataTodatStart = new Date();
    let dataTodatEnd = new Date();

    dataTodatStart.setHours(0, 0, 0);
    const [startDate, setStartDate] = useState(dataTodatStart);

    dataTodatEnd.setHours(23, 59, 59);
    const [endDate, setEndDate] = useState(dataTodatEnd);

    const dispatch = useDispatch();
    let params = useParams();
    const [value, setValue] = React.useState(0);
    const [updateOrderDialog, setUpdateOrderDialog] = useState<{
        isOpen: boolean;
        orderId: string;
        uoId: string;
        createdAtDate: any;
        order?: any;
    }>({ isOpen: false, orderId: '', uoId: '', createdAtDate: new Date().toISOString() });

    // Edit room bill dialog state
    const [editOpen, setEditOpen] = useState(false);
    const [editFormData, setEditFormData] = useState<any>({ ...EditInitialFormState });
    const [editSelectedOrderId, setEditSelectedOrderId] = useState<string | null>(null);
    const [editDisableSaveBtn, setEditDisableSaveBtn] = useState(false);
    const [editShowError, setEditShowError] = useState<any>({});
    const editPrevOrderRef = useRef<any>(null);

    const handleServiceTypeChange = (selected: any) => {
        if (selected && typeof selected.value !== 'undefined') {
            setValue(selected.value);
        } else {
            setValue(0);
        }
    };

    const handleClick = () => {
        const startDateIn = moment(startDate).utc().valueOf();
        const endDateIn = moment(endDate).utc().valueOf();
        getOrdersReport('admin', params?.restaurantId, dispatch, startDateIn, endDateIn, checked, !!value ? 'room' : 'food');
    };

    const {
        isUpdating: bulkTestUpdating,
        openBulkTestBilling: handleGstToggleAll,
        bulkTestBillingModalProps,
    } = useBulkTestBilling({
        serviceType: value,
        localReportData,
        setLocalReportData,
        dispatch,
        onRefresh: handleClick,
    });

    const {
        isDownloading: billsDownloading,
        openDownloadBills,
        downloadBillsModal,
    } = useDownloadBills({
        reports,
        restaurant,
        isRoomReport: !!value,
        startDate,
        endDate,
        dispatch,
    });

    useEffect(() => {
        if(paymentType === 'all') {
            setLocalReportData(reports);
        } else {
            setLocalReportData(reports.filter((fData: any) => fData?.selectedPayment === paymentType ));
        }
        // console.log('reports', reports);
        getAnalyticsReport(reports);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- paymentType intentionally excluded
    }, [reports]);

    useEffect(() => {
        let arr = {
            totalCost: 0,
            itemsCost: 0,
            serviceBill: 0,
            gstBill: 0,
            discount: 0,
        };
        for(let item of localReportData){
            arr.totalCost += Number(item.totalCost || 0);
            arr.itemsCost += Number(item.itemsCost || 0);
            arr.serviceBill += Number(item.serviceBill || 0);
            arr.gstBill += Number(item.gstBill || 0);
            arr.discount += Number(item.discount ||  0);
        }

        setPinnedTopRowData([{...pinnedTopRowData[0], ...arr}]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pinnedTopRowData read for merge only
    }, [localReportData]);

    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleClick intentionally excluded to avoid loop
    useEffect(() => {
        handleClick();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleClick intentionally excluded to avoid loop
    }, [value]);

    const handleChangeSwitch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setChecked(event.target.checked);
    }
    const handleDownload = () => {
        const rows = buildReportExportRows(
            reports,
            !!value,
            restaurant?.restaurantSAC,
            restaurant?.menu
        );
        if (!rows.length) {
            return;
        }
        const csvExporter = new ExportToCsv({
            ...options,
            filename: getReportExportFilename(!!value, startDate, endDate),
        });
        csvExporter.generateCsv(rows);
    };

    const handleDownloadJSON = () => {
        const rows = buildReportExportRows(
            reports,
            !!value,
            restaurant?.restaurantSAC,
            restaurant?.menu
        );
        if (!rows.length) {
            return;
        }
        const blob = new Blob([JSON.stringify(rows, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${getReportExportFilename(!!value, startDate, endDate)}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // console.log('localReportData', localReportData);
    const [pinnedTopRowData, setPinnedTopRowData]:any = useState([
        {
            orderId: 'Total:',
            uoId: '',
            guestName: '',
            tableNumber: '',
            totalCost: '',
            itemsCost: '',
            discount: '',
            gstBill: '',
            serviceBill: '',
            selectedPayment: '',
            checkInDate: '',
            checkOutDate: '',
            createdAt: '',
        },
    ]);

    // Image preview state
    const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
    const [previewImages, setPreviewImages] = useState<string[]>([]);
    const [previewIndex, setPreviewIndex] = useState(0);

    const handleOpenImagePreview = (images: string[], startIndex: number = 0) => {
        setPreviewImages(images);
        setPreviewIndex(startIndex);
        setImagePreviewOpen(true);
    };

    const handleCloseImagePreview = () => {
        setImagePreviewOpen(false);
        setPreviewImages([]);
        setPreviewIndex(0);
    };

    // Handle GST toggle
    const handleGstToggle = (orderId: string, newValue: boolean) => {
        setLocalReportData((prevData: any[]) => 
            prevData.map((item: any) => 
                item.orderId === orderId 
                    ? { ...item, isGstBill: newValue }
                    : item
            )
        );
    };

    const handlePaymentChange = async (orderId: string, paymentMode: string) => {
        if (!orderId || orderId === 'Total:') {
            return;
        }
        try {
            if (value) {
                await updateRoomOrderPayment(orderId, paymentMode);
            } else {
                await updateOrderPayment(orderId, paymentMode);
            }
            setLocalReportData((prevData: any[]) => {
                const updated = prevData.map((item: any) =>
                    item.orderId === orderId
                        ? { ...item, selectedPayment: paymentMode }
                        : item
                );
                if (paymentType !== 'all') {
                    return updated.filter((row: any) => row?.selectedPayment === paymentType);
                }
                return updated;
            });
        } catch (error) {
            console.error('Error updating payment mode:', error);
            dispatch(logError({
                severity: 'error',
                message: (error as Error)?.message || 'Failed to update payment mode',
            }));
        }
    };

    // Handle Edit: open local dialog with order data
    const handleEditRoomBill = (order: any) => {
        if (!isSuperAdmin || !order?.orderId) {
            return;
        }
        const cloned = cloneOrderForEdit(order);
        editPrevOrderRef.current = cloned;
        setEditFormData(cloned);
        setEditSelectedOrderId(order.orderId);
        setEditOpen(true);
    };

    const handleEditClose = () => {
        setEditOpen(false);
        setEditFormData({ ...EditInitialFormState });
        setEditSelectedOrderId(null);
        setEditDisableSaveBtn(false);
        setEditShowError({});
        editPrevOrderRef.current = null;
    };

    const handleEditChange = (event: any, key?: any) => {
        const { name, value: val } = event.target;
        if (name === 'roomCost') {
            const tempRooms = (editFormData?.rooms || []).map((room: any, i: number) =>
                i === key
                    ? { ...room, roomCost: val, roomCustomCost: val, roomType: restaurant?.roomType?.[val] }
                    : room
            );
            setEditFormData((prev: any) => ({ ...prev, rooms: tempRooms }));
        } else if (name === 'roomNumber') {
            const tempRooms = (editFormData?.rooms || []).map((room: any, i: number) =>
                i === key ? { ...room, roomNumber: val } : room
            );
            setEditFormData((prev: any) => ({ ...prev, rooms: tempRooms }));
        } else {
            setEditFormData((prev: any) => ({ ...prev, [name]: val }));
        }
    };

    const handleEditDateChange = (event: any, name: string) => {
        setEditFormData((prev: any) => ({ ...prev, [name]: event?.toISOString() }));
    };

    const handleEditUpdateRooms = (e: any, key: any) => {
        const { name, value: val } = e.target;
        const tempRooms = (editFormData?.rooms || []).map((room: any, i: number) =>
            i === key ? { ...room, [name]: val } : room
        );
        setEditFormData((prev: any) => ({ ...prev, rooms: tempRooms }));
    };

    const editAddRooms = () => {
        setEditFormData((prev: any) => ({
            ...prev, rooms: [...(prev?.rooms || []), { roomType: '', roomCost: '', roomCustomCost: '', noOfNights: 1, roomNumber: '' }]
        }));
    };

    const editRemoveRooms = (roomIndex: any) => {
        const tempRooms = editFormData?.rooms?.filter((_: any, key: any) => key !== roomIndex);
        setEditFormData((prev: any) => ({ ...prev, rooms: [...tempRooms] }));
    };

    const handleEditFileUpload = (urls: string[]) => {
        setEditFormData((prev: any) => ({
            ...prev,
            identificationFiles: [...(prev.identificationFiles || []), ...urls].slice(0, 4),
        }));
    };

    const handleEditFileRemove = (url: string) => {
        setEditFormData((prev: any) => ({
            ...prev,
            identificationFiles: (prev.identificationFiles || []).filter((f: string) => f !== url),
        }));
    };

    // Compute GST rate from configurable slab
    const getGstRateFromSlab = (amount: number): number => {
        if (typeof amount !== 'number' || isNaN(amount) || !isFinite(amount) || amount < 0) return 0;
        const slab: any = roomGSTSlab;
        let resolvedRate = 0;
        if (Array.isArray(slab)) {
            for (const band of slab) {
                const minAmt = typeof band?.minAmount === 'number' ? band.minAmount : NaN;
                const rate = typeof band?.rate === 'number' ? band.rate : NaN;
                if (!isNaN(minAmt) && !isNaN(rate) && amount >= minAmt) resolvedRate = rate;
            }
            return resolvedRate;
        }
        if (slab && typeof slab === 'object') {
            const thresholds = Object.keys(slab).map(Number).filter((n) => !isNaN(n) && amount >= n).sort((a, b) => a - b);
            if (thresholds.length > 0) {
                resolvedRate = Number(slab[thresholds[thresholds.length - 1]]);
                if (isNaN(resolvedRate)) resolvedRate = 0;
            }
            return resolvedRate;
        }
        return typeof restaurant?.roomGST === 'number' ? restaurant.roomGST : 0;
    };

    const handleEditSubmit = (event: any) => {
        event.preventDefault();
        const { roomGST, ownerId, gstIn, roomService, name: restName, foodType, address, docId } = restaurant;
        const { email, phoneNumber, uid } = user;

        const itemsCost = editFormData?.rooms?.reduce((sum: any, obj: any) => sum + (obj?.roomCustomCost * obj?.noOfNights || 0), 0);
        const discountedAmount = Math.max(itemsCost - (+editFormData?.discount || 0), 0);
        const totalNightsSpent = editFormData?.rooms?.reduce((sum: any, obj: any) => sum + (+obj?.noOfNights || 0), 0);
        const amountPerNight = totalNightsSpent > 0 ? discountedAmount / totalNightsSpent : discountedAmount;
        const gstRateRaw = getGstRateFromSlab(amountPerNight);
        const gstRate: number = (typeof gstRateRaw === 'number' && !isNaN(gstRateRaw) && isFinite(gstRateRaw)) ? gstRateRaw : 0;
        const isGstBill = editFormData?.isGstBill !== undefined ? editFormData.isGstBill : true;
        const gstBill: number = isGstBill ? Number(((discountedAmount * gstRate) / 100).toFixed(2)) : 0;
        const serviceBill: number = (isGstBill && roomService) ? Number((discountedAmount * roomService / 100).toFixed(2)) : 0;
        const totalCost: number = discountedAmount + gstBill + serviceBill;

        if (editFormData?.guestName) {
            setEditDisableSaveBtn(true);
            try {
                // Remove createdAt from update data — the report transforms it to a
                // formatted string, so writing it back would corrupt the original timestamp.
                const { createdAt: _dropCreatedAt, ...editDataWithoutCreatedAt } = editFormData;
                const billPayload = {
                    ...editDataWithoutCreatedAt,
                    restaurantId: docId,
                    orderId: editSelectedOrderId,
                    uoId: editFormData?.uoId,
                    updatedAt: moment().utc().valueOf(),
                    userId: uid,
                    isGstBill,
                    selectedPayment: editFormData?.selectedPayment || 'cash',
                    itemsCost,
                    gstBill,
                    gstRate,
                    serviceBill,
                    totalCost,
                    identificationFiles: editFormData?.identificationFiles || [],
                    restaurant: {
                        roomGST: (isGstBill && roomGST) ? roomGST : 0, ownerId, gstIn: isGstBill ? gstIn : '*******', roomService: (isGstBill && roomService) ? roomService : 0, name: restName, foodType, address, docId
                    },
                    user: { email, phoneNumber, uid }
                };
                const previous = buildRoomBillSnapshot(editPrevOrderRef.current || editFormData);
                const next = buildRoomBillSnapshot(billPayload);
                const clientRequestId =
                    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                        ? crypto.randomUUID()
                        : `room-upd-report-${Date.now()}`;

                createRoomBills(billPayload, dispatch, null, params?.restaurantId).then((result) => {
                    const ok = result !== false;
                    if (editSelectedOrderId) {
                        logOrderEventAsync({
                            restaurantId: String(docId || params?.restaurantId || 'unknown'),
                            orderId: editSelectedOrderId,
                            collectionName: 'room-bills',
                            orderKind: 'room',
                            action: 'update',
                            status: ok ? 'success' : 'failed',
                            source: 'admin-report',
                            errorMessage: ok ? null : 'Failed to update room bill',
                            previous,
                            next,
                            snapshot: next,
                            clientRequestId,
                        });
                    }
                    if (ok) {
                        handleEditClose();
                        handleClick(); // Refresh report
                    } else {
                        setEditDisableSaveBtn(false);
                    }
                });
            } catch (e) {
                console.log('failed to call');
                setEditDisableSaveBtn(false);
            }
        } else {
            setEditShowError({ guestName: editFormData?.guestName ? '' : 'Required field.' });
        }
    };

    const handleEditFoodOrder = (order: any) => {
        if (!isSuperAdmin || !order?.orderId) {
            return;
        }
        setUpdateOrderDialog({
            isOpen: true,
            orderId: order.orderId,
            uoId: order.uoId || order.orderId,
            createdAtDate: order.createdAt,
            order,
        });
    };

    const handleCloseFoodOrderDialog = (isUpdated: boolean) => {
        setUpdateOrderDialog({
            isOpen: false,
            orderId: '',
            uoId: '',
            createdAtDate: new Date().toISOString(),
        });
        if (isUpdated) {
            handleClick();
        }
    };

    const handlePrintFoodBill = async (order: any) => {
        try {
            await printFoodOrderBill(order, restaurant);
        } catch (error) {
            const message =
                (error as Error)?.message === 'Nothing to print'
                    ? 'Nothing to Print :)'
                    : (error as Error)?.message || 'Failed to print bill';
            dispatch(logError({
                severity: (error as Error)?.message === 'Nothing to print' ? 'info' : 'error',
                message,
            }));
        }
    };

    const handlePrintRoomPdf = async (order: any) => {
        if (!order?.orderId) {
            return;
        }
        try {
            const htmlContent = restaurant?.esign
                ? await getRoomPdfHTMLStrings(order, restaurant)
                : await getRoomPdfHTMLString(order, restaurant);
            await printPDF(htmlContent, order?.uoId || order?.orderId);
        } catch (error) {
            console.error('Error printing room PDF:', error);
            dispatch(logError({
                severity: 'error',
                message: (error as Error)?.message || 'Failed to print room PDF',
            }));
        }
    };

    // Get updated column definitions with super admin state
    const updatedColDefs = getUpdatedColDefs(
        isSuperAdmin,
        !!value,
        handleGstToggle,
        handleOpenImagePreview,
        handleEditRoomBill,
        handleEditFoodOrder,
        handlePrintFoodBill,
        handlePrintRoomPdf,
        handlePaymentChange,
    );

    const handleChangePayType = (val: any) => {
        setPaymentType(val?.value);

        if(val?.value === 'all') {
            setLocalReportData(reports);
        } else {
            setLocalReportData(reports.filter((fData: any) => fData?.selectedPayment === val?.value ));
        }
    };

    return (
        <div className='mobReportWrap'>
            <div className='resportHeader'>
                <Grid container spacing={2} columns={12}>
                    {/* Filters row: service type, date range, payment mode, mode toggle */}
                    <Grid item xs={12}>
                        <div className="filterRow filterRow--secondary reportFilters">
                            <div className="filterBlock filterBlock--inline filterBlock--service">
                                <div className="filterLabel">Service Type</div>
                                <ReactSelect
                                    className="filterTypes"
                                    defaultValue={serviceTypeOptions[0]}
                                    options={serviceTypeOptions}
                                    onChange={handleServiceTypeChange}
                                />
                            </div>
                            <div className="filterBlock filterBlock--inline filterBlock--dates">
                                <div className="filterLabel">Date Range (From - To)</div>
                                <div className="dateRange">
                                    <div className="dateField">
                                        <DatePicker
                                            selected={startDate}
                                            onChange={(date: Date | null) => date && setStartDate(date)}
                                            selectsStart
                                            startDate={startDate}
                                            endDate={endDate}
                                            dateFormat="d MMM yyyy"
                                            placeholderText="Start date"
                                        />
                                    </div>
                                    <div className="dateField">
                                        <DatePicker
                                            selected={endDate}
                                            onChange={(date: Date | null) => {
                                                if (date) {
                                                    const end = new Date(date);
                                                    end.setHours(23, 59, 59, 999);
                                                    setEndDate(end);
                                                }
                                            }}
                                            selectsEnd
                                            startDate={startDate}
                                            endDate={endDate}
                                            minDate={startDate}
                                            dateFormat="d MMM yyyy"
                                            placeholderText="End date"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="filterBlock filterBlock--inline filterBlock--payment">
                                <div className="filterLabel">Payment Mode</div>
                                <ReactSelect
                                    key={"shgdsh0909"}
                                    className="filterTypes"
                                    defaultValue={FilterPaymentType[0]}
                                    options={FilterPaymentType}
                                    onChange={handleChangePayType}
                                />
                            </div>
                            {isSuperAdmin && (
                                <div className="filterBlock filterBlock--inline filterBlock--mode">
                                    <div className="filterLabel">Report Mode</div>
                                    <div className="billToggle billToggle--card">
                                        <span className="billToggle__label">
                                            {checked ? 'Live orders' : 'Test billing'}
                                        </span>
                                        <Switch
                                            color='secondary'
                                            checked={!!checked}
                                            onChange={handleChangeSwitch}
                                            {...label}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </Grid>

                    {/* Total bills + action buttons row */}
                    <Grid item xs={12}>
                        <div className="filterRow filterRow--primary actionButtons">
                            <div className="filterBlock">
                                <div className="totalText">
                                    Total Bills: [ {localReportData?.length} ]
                                </div>
                            </div>
                            <div className="filterBlock filterBlock--right">
                                {isSuperAdmin && !value && (
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        onClick={handleGstToggleAll}
                                        disabled={bulkTestUpdating}
                                    >
                                        {bulkTestUpdating ? 'Updating…' : 'Set all to Test-Billing'}
                                    </Button>
                                )}
                                <ButtonGroup variant="outlined" color="primary" className="actionButtons__group">
                                    <Button
                                        className='btnReport'
                                        variant='contained'
                                        color='primary'
                                        onClick={handleClick}
                                    >
                                        View Report
                                    </Button>
                                    <Button
                                        disabled={!reports?.length}
                                        className='btnReport'
                                        onClick={handleDownload}
                                    >
                                        D-CSV
                                    </Button>
                                    <Button
                                        disabled={!reports?.length}
                                        className='btnReport'
                                        onClick={handleDownloadJSON}
                                    >
                                        D-JSON
                                    </Button>
                                    <Button
                                        disabled={!reports?.length || billsDownloading}
                                        className='btnReport'
                                        onClick={openDownloadBills}
                                    >
                                        {billsDownloading ? 'Generating…' : 'D-Bills'}
                                    </Button>
                                </ButtonGroup>
                            </div>
                        </div>
                    </Grid>
                </Grid>
            </div>
            {!!localReportData && (
                <AgGrid
                    rowData={localReportData}
                    colDefs={!!value ? updatedColDefs.roomBills : updatedColDefs.orderBills}
                    pinnedTopRowData={pinnedTopRowData}
                    suppressRowClickSelection
                    suppressCellFocus
                />
            )}
            {/* <Analytics /> */}
            <ImagePreviewModal
                open={imagePreviewOpen}
                onClose={handleCloseImagePreview}
                images={previewImages}
                initialIndex={previewIndex}
            />
            {/* Edit Room Bill Dialog */}
            <Dialog fullScreen open={editOpen} onClose={handleEditClose} TransitionComponent={EditTransition}>
                <AppBar sx={{ position: 'relative' }}>
                    <Toolbar>
                        <IconButton edge="start" color="inherit" onClick={handleEditClose} aria-label="close">
                            <CloseIcon />
                        </IconButton>
                        <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                            Edit Guest Information
                        </Typography>
                        <Button variant='contained' color="primary" onClick={handleEditClose}>Cancel</Button>
                        <Button disabled={editDisableSaveBtn} variant='contained' color="primary" type="submit" onClick={handleEditSubmit}>Save</Button>
                    </Toolbar>
                </AppBar>
                <Grid className="dialogWrap" container spacing={2} padding={2}>
                    <Grid item xs={12}>
                        <Typography flexGrow={2} variant="h6">Guest Details</Typography>
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <TextField error={!!editShowError?.guestName} fullWidth label="Guest Name" name="guestName" value={editFormData.guestName} onChange={handleEditChange} helperText={editShowError?.guestName} />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <TextField fullWidth label="Mobile" name="guestMobile" value={editFormData.guestMobile} onChange={handleEditChange} type="number" />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <TextField fullWidth label="Guest Address" name="guestAddress" value={editFormData.guestAddress} onChange={handleEditChange} />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <TextField fullWidth label="Other Guests" name="guestOther" value={editFormData.guestOther} onChange={handleEditChange} />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <TextField fullWidth label="Extra Information" name="guestExtraInfo" value={editFormData.guestExtraInfo} onChange={handleEditChange} />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <TextField fullWidth label="Guest Company Name" name="guestCompanyName" value={editFormData.guestCompanyName} onChange={handleEditChange} />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <TextField fullWidth label="Guest Company GST" name="guestCompanyGST" value={editFormData.guestCompanyGST} onChange={handleEditChange} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Any Discount" type="number" name="discount" value={editFormData.discount} onChange={handleEditChange} InputProps={{ inputProps: { min: 0, step: 1 } }} />
                    </Grid>
                    <Grid container item xs={12} justifyContent="space-between" gap="2">
                        <Grid item xs={12} sm={6} sx={{ pr: '10px' }}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DemoContainer components={['DateTimePicker']}>
                                    <DemoItem label={<DateLabel componentName="Check-in Date & Time" />}>
                                        <DateTimePicker value={dayjs(editFormData?.checkInDate)} onChange={(e) => handleEditDateChange(e, 'checkInDate')} />
                                    </DemoItem>
                                </DemoContainer>
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6} sx={{ pr: '10px' }}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DemoContainer components={['DateTimePicker']}>
                                    <DemoItem label={<DateLabel componentName="Check-out Date & Time" />}>
                                        <DateTimePicker value={dayjs(editFormData?.checkOutDate)} onChange={(e) => handleEditDateChange(e, 'checkOutDate')} />
                                    </DemoItem>
                                </DemoContainer>
                            </LocalizationProvider>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px' }}>
                        <div style={{ margin: '0.5rem' }}>Rooms:
                            {editFormData?.rooms?.map((room: any, key: any) =>
                                !!room && <Box className='roomListMulti' key={room?.roomType + key}>
                                    <MuiButtonGroup size="small" aria-label="Small button group" className='formWrapColl roomsList'>
                                        <FormControl sx={{ width: '200px' }}>
                                            <InputLabel>Room Type</InputLabel>
                                            <MuiSelect value={room?.roomCost} onChange={(e) => handleEditChange(e, key)} name="roomCost" key={room?.roomType + key}>
                                                {!!restaurant?.roomType && Object.entries(restaurant?.roomType)?.map(([val, lbl]: any) => (
                                                    <MenuItem key={val} value={val}>{`${lbl}-${val}`}</MenuItem>
                                                ))}
                                            </MuiSelect>
                                        </FormControl>
                                        <TextField sx={{ width: '140px' }} key={"roomCustomCost" + key} className='input-type' onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEditUpdateRooms(e, key)} name="roomCustomCost" value={room.roomCustomCost} label="Room Cost" type="number" />
                                        <TextField key={"noOfNights" + key} sx={{ width: '140px' }} className='input-type' onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEditUpdateRooms(e, key)} name="noOfNights" value={room.noOfNights} label="No of Nights" type="number" InputProps={{ inputProps: { min: 1, step: 1 } }} />
                                        <MuiSelect sx={{ width: '140px' }} value={room?.roomNumber || ''} onChange={(e) => handleEditChange(e, key)} name="roomNumber" key={room?.roomType + room?.roomNumber + key}>
                                            {!!roomList?.length && roomList?.map((r: any) => (
                                                <MenuItem key={r?.value} value={r?.value}>{r?.label}</MenuItem>
                                            ))}
                                        </MuiSelect>
                                        <Button className='btnDelete' color='error' onClick={() => editRemoveRooms(key)}><DeleteIcon /></Button>
                                    </MuiButtonGroup>
                                </Box>
                            )}
                            <Button variant='outlined' color="secondary" onClick={() => editAddRooms()}>+ Add New</Button>
                        </div>
                    </Grid>
                    {/* Guest ID upload & preview */}
                    <Grid item xs={12} style={{ display: 'flex', flexDirection: 'column', gap: '12px', backgroundColor: 'var(--primary-light)', padding: '10px' }}>
                        <FileUpload
                            onFileUpload={handleEditFileUpload}
                            onFileRemove={handleEditFileRemove}
                            restaurantId={params?.restaurantId || restaurant?.docId}
                            uoId={editFormData?.uoId}
                            guestName={editFormData?.guestName}
                            existingUrls={editFormData?.identificationFiles || []}
                            maxFiles={4}
                            maxFileSizeMB={5}
                        />
                    </Grid>
                </Grid>
                <Grid item xs={12} style={{ display: 'flex', justifyContent: 'space-between', backgroundColor: 'var(--primary-light)', padding: '10px' }}>
                    <Button disabled={editDisableSaveBtn} onClick={handleEditSubmit} variant="contained" color="primary">Save</Button>
                </Grid>
            </Dialog>
            <BulkTestBillingModal {...bulkTestBillingModalProps} />
            {downloadBillsModal}
            {isSuperAdmin && !value && (
                <UpdateOrderDialog
                    data={updateOrderDialog}
                    onClose={handleCloseFoodOrderDialog}
                />
            )}
        </div>
    );
};

const mapStateToProps = (state: any) => {
    const { common, user } = state;
    let roomNumArr: any = [];
    const tempTabs: any = common?.selectedRestaurant?.tabs;
    const regex = new RegExp("ROOM", "i");
    for (let roomNum in tempTabs) {
        if (regex.test(roomNum)) {
            for (let i = 1; i <= tempTabs[roomNum]; i++) {
                roomNumArr.push({ label: roomNum + i, value: roomNum + '-' + i });
            }
        }
    }

    return {
        reports: common?.ordersReport || [],
        restaurant: common?.selectedRestaurant,
        user: user?.user || {},
        roomList: roomNumArr || [],
        roomGSTSlab: common?.selectedRestaurant?.roomGSTSlab || {},
        isSuperAdmin: !!(user?.user?.isSuperAdmin)
    }
}

export default connect(mapStateToProps)(Reports)
